'use client';

import { Code } from 'shared/ui/code';
import { useSession } from '@/app/_providers/session-provider';
import { Link } from 'shared/ui/link';
import { DocsIconHelper } from 'shared/ui/tooltips';
import { Alert, AlertDescription } from 'shared/ui/alert';
import { Separator } from 'shared/ui/separator';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import mergeClasses from 'shared/lib/utils';
import { useMemo } from 'react';
import * as m from '@paraglide/messages';
import { DOCS_DOMAIN, IS_INTERNATIONAL } from 'shared/lib/util';
const listVariants = cva('', {
  variants: {
    listVariant: {
      default: 'list-disc ps-10',
      'no-disc': ''
    }
  },
  defaultVariants: {
    listVariant: 'default'
  }
});
interface RepoInfoProps extends VariantProps<typeof listVariants> {
  slug: string;
}
const projectName = IS_INTERNATIONAL() ? 'amverum' : 'amvera';
export default function RepoInfo({
  slug,
  listVariant
}: RepoInfoProps) {
  const session = useSession();
  const username = session?.user.name;
  const isModel = useMemo(() => listVariant === 'no-disc', [listVariant]);
  return <div className="flex flex-col gap-9" data-sentry-component="RepoInfo" data-sentry-source-file="repo-info.tsx">
      <div className="max-w-5xl space-y-5">
        <ul className={mergeClasses(listVariants({
        listVariant
      }), 'w-full')}>
          <li className="w-full">
            <div className="flex flex-row flex-wrap items-center justify-between gap-2 w-full">
              <div className="flex gap-2 md:min-w-96 items-center shrink-0">
                <span className="text-body2">
                  {m.tab_repo_connection_primary()}
                </span>
                <DocsIconHelper href={`${DOCS_DOMAIN}/applications/git/main-origin.html`} data-sentry-element="DocsIconHelper" data-sentry-source-file="repo-info.tsx" />
              </div>

              <Code className={isModel ? 'w-full' : 'w-0 flex-1'} prefix="$" text={`git clone https://git.${process.env.GIT_BASE_URI}/${username}/${slug}`} data-sentry-element="Code" data-sentry-source-file="repo-info.tsx" />
            </div>
          </li>
          <p className="my-4 text-body2">
            <b>{m.tab_repo_connection_or()}</b>
          </p>
          <li>
            <div className="flex flex-row flex-wrap items-center justify-between gap-2">
              <div className="flex gap-2 md:min-w-96 items-center">
                <span className="text-body2">
                  {m.tab_repo_connection_existing()}
                </span>
                <DocsIconHelper href={`${DOCS_DOMAIN}/applications/git/secondary-origin.html`} data-sentry-element="DocsIconHelper" data-sentry-source-file="repo-info.tsx" />
              </div>

              <Code className={isModel ? 'w-full' : 'flex-1 min-w-0'} prefix="$" text={`git remote add ${projectName} https://git.${process.env.GIT_BASE_URI}/${username}/${slug}`} data-sentry-element="Code" data-sentry-source-file="repo-info.tsx" />
            </div>
          </li>
        </ul>
        <Separator className="" data-sentry-element="Separator" data-sentry-source-file="repo-info.tsx" />
        <ul className={mergeClasses(listVariants({
        listVariant
      }), 'mb-5')}>
          <li>
            <div className="flex flex-row flex-wrap items-center justify-between gap-2">
              <span className="md:min-w-96 text-body2">
                {m.tab_repo_connection_submit()}
              </span>
              <Code className={isModel ? 'w-full' : 'flex-1 min-w-0'} prefix="$" text={`git push ${projectName} master`} data-sentry-element="Code" data-sentry-source-file="repo-info.tsx" />
            </div>
          </li>
          <p className="my-4 text-body2">
            <b>{m.tab_repo_connection_or()}</b>
          </p>
          <li>
            <div className="flex flex-row flex-wrap items-center justify-between gap-2">
              <span className="md:min-w-96 text-body2">
                {m.tab_repo_connection_ifPrimary()}
              </span>
              <Code className={isModel ? 'w-full' : 'flex-1 min-w-0'} prefix="$" text="git push" data-sentry-element="Code" data-sentry-source-file="repo-info.tsx" />
            </div>
          </li>
        </ul>
      </div>
      <Alert size="sm" className="w-fit" data-sentry-element="Alert" data-sentry-source-file="repo-info.tsx">
        <AlertDescription data-sentry-element="AlertDescription" data-sentry-source-file="repo-info.tsx">
          <Link href={`${DOCS_DOMAIN}/applications/git/freq-errors.html`} docs data-sentry-element="Link" data-sentry-source-file="repo-info.tsx">
            {m.tab_repo_helper()}
          </Link>
        </AlertDescription>
      </Alert>
    </div>;
}